/**
 * Kiosk auth for the TV job board.
 *
 * The app applies the `auth` middleware globally, so a display box that reboots
 * or whose session expires would land on a login page and stay there. The board
 * page sets `auth: false` to skip that redirect; this middleware supplies the
 * credential: a long-lived "Board Display" Sanctum token, scoped server-side to
 * the routes the TV calls (RejectBoardToken).
 *
 * The token arrives once via the URL fragment (`/job-board#board_token=...`),
 * is stored per-device in localStorage, and is sent as a Bearer token on the
 * board's requests thereafter. The fragment is stripped from the URL so the
 * token is not left on screen or in history.
 *
 * The token is used ONLY on the TV page itself, and only when nobody is signed
 * in. It is taken off axios on every other route. This matters because the API
 * does not start a session for bearer requests (that is what lets the kiosk
 * write without a CSRF cookie): a token left on axios would make a signed-in
 * person -- someone who once opened the kiosk link in their own browser --
 * act as "Board Display" everywhere, remote included, and lose their own
 * permissions.
 */
const TV_PATH = "/job-board";

function storedToken() {
  try {
    return localStorage.getItem("boardToken");
  } catch (e) {
    return null;
  }
}

export default function ({ route, $axios, $auth }) {
  if (process.server) return;

  const onTv = route.path === TV_PATH || route.path === TV_PATH + "/";

  let token = null;

  if (onTv) {
    const match = (window.location.hash || "").match(/board_token=([^&]+)/);
    if (match) {
      token = decodeURIComponent(match[1]);
      try {
        localStorage.setItem("boardToken", token);
      } catch (e) {
        /* storage unavailable -- fall back to in-memory for this session */
      }
      // Strip the token from the visible URL / browser history. route.fullPath
      // still carries the fragment, so build the URL without it.
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, "", window.location.pathname + (window.location.search || ""));
      }
    } else {
      token = storedToken();
    }
  }

  if (!$axios || typeof $axios.setToken !== "function") return;

  const signedIn = !!($auth && $auth.loggedIn);

  if (onTv && token && !signedIn) {
    $axios.setToken(token, "Bearer");
    return;
  }

  // Take the board token off -- but only our own header, never one set by
  // something else.
  const ours = storedToken() || token;
  const common = ($axios.defaults && $axios.defaults.headers && $axios.defaults.headers.common) || {};
  if (ours && common.Authorization === "Bearer " + ours) {
    $axios.setToken(false);
  }
}
